// src/features/sales/salesApiSlice.js
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

// --- Normalized adapter ---
const salesAdapter = createEntityAdapter({
  // use our own string "id" we set in transformResponse
  selectId: (c) => c.id,
});

// Keep a stable extra field for pagination metadata
const initialState = salesAdapter.getInitialState({ total: 0 });

export const salesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSales: builder.query({
        // args: { page, limit, q, status, sortBy, order, ... }
        query: (params = {}) => ({ url: '/sales', params }),

        transformResponse: (response, meta) => {
          let items = [];
          let total = 0;

          if (response && typeof response === 'object' && 'items' in response && 'total' in response) {
            items = response.items ?? [];
            total = Number(response.total ?? 0);
          } else if (Array.isArray(response)) {
            items = response;
            const headerTotal =
              meta?.response?.headers?.get('X-Total-Count') ??
              meta?.response?.headers?.get('x-total-count');
            total = headerTotal ? Number(headerTotal) : items.length;
          }

          // 1) Force a *single* stable string key, and set BOTH id and _id to it
          const processed = items.map((c) => {
            const raw = c?._id ?? c?.id;
            const sid = raw != null ? String(raw) : undefined;
            return { ...c, id: sid, _id: sid };
          });

          // 2) Manual normalization using *id* as the key (matches adapter.selectId)
          const ids = [];
          const entities = {};
          for (const c of processed) {
            if (c.id) {
              ids.push(c.id);
              entities[c.id] = c;
            }
          }

          // Optional debug (remove after verifying)
          // console.log('normalized (id):', { idsLen: ids.length, total, first: ids[0] });

          return { ids, entities, total };
        },
      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Sale', id })),
              { type: 'Sale', id: 'LIST' }, // ⬅️ use LIST consistently
            ]
          : [{ type: 'Sale', id: 'LIST' }],
    }),

    getSalesWithDistinct: builder.query({
      // args: { user, q, from, to, ... }
      query: (params = {}) => ({ url: '/sales/distinct', params }),

      transformResponse: (response) => {
        // Backend always returns an array for this endpoint
        const items = Array.isArray(response) ? response : [];

        const ids = [];
        const entities = {};

        for (const row of items) {
          const main = row.main_company ?? 'UNKNOWN';
          const ym = row.yearMonth ?? '000000';
          // Composite synthetic ID
          const id = `${main}__${ym}`;

          const entity = {
            ...row,
            id,      // required by adapter
            _id: id, // optional, just to be consistent
          };

          ids.push(id);
          entities[id] = entity;
        }

        return {
          ids,
          entities,
          total: items.length, // total "group rows"
        };
      },

      // Separate tag type is cleaner; but you can keep 'Sale' if you like
      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'SaleSummary', id })),
              { type: 'SaleSummary', id: 'LIST' },
            ]
          : [{ type: 'SaleSummary', id: 'LIST' }],
    }),


    getSaleById: builder.query({
      query: (id) => `/sales/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Sale', id }],
    }),

    addNewSale: builder.mutation({
      query: (body) => ({ url: '/sales', method: 'POST', body }),
      invalidatesTags: [{ type: 'Sale', id: 'LIST' }], // ⬅️ matches providesTags
    }),

    updateSale: builder.mutation({
      query: ({ id, ...patch }) => ({ url: `/sales/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'Sale', id }, { type: 'Sale', id: 'LIST' }],
    }),

    deleteSale: builder.mutation({
      query: ({ id }) => ({ url: `/sales/${id}`, method: 'DELETE', body: { id } }),
      invalidatesTags: [{ type: 'Sale', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSalesQuery,
  useGetSalesWithDistinctQuery,
  useGetSaleByIdQuery,
  useAddNewSaleMutation,
  useUpdateSaleMutation,
  useDeleteSaleMutation,
} = salesApiSlice;

/* -----------------------
   Arg-aware selectors
   ----------------------- */

// Base result selector factory (must pass the same args you use in the hook)
const selectGetSalesResult = salesApiSlice.endpoints.getSales.select;

export const makeSelectSalesData = (args) =>
  createSelector(selectGetSalesResult(args), (res) => res?.data ?? initialState);

// Export a factory that returns normalized selectors + total for a given args set
export const makeSaleSelectors = (args) => {
  const selectData = makeSelectSalesData(args);
  const base = salesAdapter.getSelectors((state) => selectData(state));
  return {
    selectAllSales: base.selectAll,
    selectSaleById: base.selectById,
    selectSaleIds: base.selectIds,
    selectSaleEntities: base.selectEntities,
    selectTotal: (state) => selectData(state).total ?? 0,
  };
};

// ---------- Distinct / summary selectors ----------

// Base result selector for the distinct endpoint
const selectGetSalesDistinctResult = salesApiSlice.endpoints.getSalesWithDistinct.select;

// We can reuse the same adapter's initialState shape
const initialDistinctState = salesAdapter.getInitialState({ total: 0 });

export const makeSelectSalesDistinctData = (args) =>
  createSelector(selectGetSalesDistinctResult(args), (res) => res?.data ?? initialDistinctState);

export const makeSaleDistinctSelectors = (args) => {
  const selectData = makeSelectSalesDistinctData(args);
  const base = salesAdapter.getSelectors((state) => selectData(state));
  return {
    selectAllSalesSummary: base.selectAll,
    selectSalesSummaryById: base.selectById,
    selectSalesSummaryIds: base.selectIds,
    selectSalesSummaryEntities: base.selectEntities,
    selectSummaryTotal: (state) => selectData(state).total ?? 0,
  };
};
